import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  useTheme,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon, Edit as EditIcon } from "@mui/icons-material";
import NumericTextField from "./NumericTextField";
import FormTextField from "../form/FormTextField";
import CustomTypography from "../form/CustomTypography";
import AppButton from "../buttons/AppButton";
import AppDropDown from "../filter/AppDropDown";

interface SingleInputProps {
  open: boolean;
  onClose: () => void;
  heading?: string;
  content?: string;
  type: string;
  onSubmit: (value: any) => void;
  height?: string;
  initialValue?: string;
  optionalText?: string;
  buttonMarginTop?: string | number;
  textFieldMarginTop?: string;
  key?: string;
  required?: boolean;
  /** When true, submit calls onSubmit even if the field is empty (e.g. optional reason). */
  allowEmptySubmit?: boolean;
  multiline?: boolean;
  selectOptions?: number[];
  zIndex?: number;
}

const SingleInput: React.FC<SingleInputProps> = ({
  open,
  onClose,
  heading,
  content,
  type,
  onSubmit,
  optionalText,
  buttonMarginTop = 3,
  textFieldMarginTop = "1.5rem",
  key,
  required,
  allowEmptySubmit,
  multiline,
  selectOptions,
  zIndex = 2000,
}) => {
  const theme = useTheme();
  const [value, setValue] = useState<string>("");

  const handleSubmit = () => {
    if (allowEmptySubmit) {
      onSubmit(value.trim());
      setValue("");
    } else if (required && required === true) {
      onSubmit(value);
      setValue("");
    } else if (value !== "" && value !== undefined) {
      onSubmit(value);
      setValue("");
    }
    onClose();
  };

  return (
    <Dialog
      key={key}
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        transition: {
          timeout: 300,
          sx: {
            backgroundColor:
              theme.palette.customColors?.backdrop || "rgba(0, 0, 0, 0.4)",
            backdropFilter: "blur(4px)",
          },
        },
        backdrop: {
          timeout: 300,
          sx: {
            backgroundColor:
              theme.palette.customColors?.backdrop || "rgba(0, 0, 0, 0.4)",
            backdropFilter: "blur(4px)",
          },
        },
        paper: {
          sx: {
            borderRadius: "16px",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
            overflow: "hidden",
            maxHeight: "90vh",
            maxWidth: {
              xs: "95vw",
              sm: "90vw",
              md: "500px",
            },
            width: {
              xs: "95vw",
              sm: "90vw",
              md: "500px",
            },
            margin: { xs: "16px", sm: "32px" },
            background: `linear-gradient(135deg, ${
              theme.palette.customColors?.white ||
              theme.palette.background.paper
            } 0%, ${
              theme.palette.customColors?.lightGreyColor ||
              theme.palette.grey[50]
            } 100%)`,
          },
        },
      }}
      sx={{
        zIndex,
        "& .MuiDialog-container": {
          alignItems: "center",
          justifyContent: "center",
        },
        "& .MuiDialog-paper": {
          zIndex,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: `linear-gradient(90deg, ${
              theme.palette.customColors?.blueGradientStart ||
              theme.palette.primary.main
            } 0%, ${
              theme.palette.customColors?.blueGradientEnd ||
              theme.palette.primary.dark
            } 50%, ${
              theme.palette.customColors?.blueGradientStart ||
              theme.palette.primary.main
            } 100%)`,
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: { xs: "12px 16px 12px", sm: "16px 20px 16px" },
            borderBottom: `1px solid ${
              theme.palette.customColors?.greyBorder || theme.palette.divider
            }`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                backgroundColor: theme.palette.primary.main + "15",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <EditIcon sx={{ color: "primary.main", fontSize: 20 }} />
            </Box>
            <Box>
              <CustomTypography
                variant="heading4"
                fontWeight={600}
                color={
                  theme.palette.customColors?.blackBorder || "text.primary"
                }
              >
                {heading || "Edit Field"}
              </CustomTypography>
              {content && (
                <CustomTypography
                  variant="body2"
                  color={
                    theme.palette.customColors?.greyTextColor ||
                    "text.secondary"
                  }
                  sx={{ mt: 0.5 }}
                >
                  {content}
                </CustomTypography>
              )}
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color:
                theme.palette.customColors?.greyTextColor ||
                theme.palette.text.secondary,
              "&:hover": {
                backgroundColor:
                  theme.palette.customColors?.lightGreyColor ||
                  theme.palette.grey[100],
                color:
                  theme.palette.customColors?.blackBorder ||
                  theme.palette.text.primary,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <DialogContent
          sx={{
            padding: { xs: "20px", sm: "24px" },
            flex: 1,
            overflow: "auto",
            maxHeight: "60vh",
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor:
                theme.palette.customColors?.greyBackground ||
                theme.palette.grey[100],
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor:
                theme.palette.customColors?.greyDarkborder ||
                theme.palette.grey[400],
              borderRadius: "4px",
              "&:hover": {
                backgroundColor:
                  theme.palette.customColors?.greyTextColor ||
                  theme.palette.grey[600],
              },
            },
          }}
        >
          {optionalText && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                backgroundColor: theme.palette.info.main + "10",
                borderRadius: 2,
                border: `1px solid ${theme.palette.info.main + "30"}`,
              }}
            >
              <CustomTypography
                variant="body2"
                color="info.main"
                fontWeight={500}
              >
                {optionalText}
              </CustomTypography>
            </Box>
          )}

          <Box sx={{ mb: 3 }}>
            {type === "number" && (
              <NumericTextField
                label={content}
                fullWidth
                margin="normal"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />
            )}
            {type === "text" && (
              <FormTextField
                sx={{
                  marginTop: textFieldMarginTop,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
                rows={multiline ? 4 : 1}
                multiline={multiline ? true : false}
                label={content}
                value={value}
                onChange={(id, e) => {
                  if (e !== undefined) {
                    setValue(e);
                  }
                }}
              />
            )}
            {type === "select" && (
              <Box mt={2}>
                <AppDropDown
                  isMultiSelect={false}
                  title=""
                  height="48px"
                  listHeight="40px"
                  value={value}
                  onSingleSelectionChange={(e) => setValue(e)}
                  list={selectOptions?.map((option) => ({
                    id: option.toString(),
                    value: option.toString(),
                  }))}
                  id={type || "select"}
                  menuZIndex={3000}
                />
              </Box>
            )}
          </Box>
        </DialogContent>

        {/* Footer Actions */}
        <DialogActions
          sx={{
            padding: { xs: "16px 20px 20px", sm: "20px 24px 24px" },
            borderTop: `1px solid ${
              theme.palette.customColors?.greyBorder || theme.palette.divider
            }`,
            backgroundColor:
              theme.palette.customColors?.lightGreyColor ||
              theme.palette.grey[50],
            gap: 2,
            justifyContent: "flex-end",
          }}
        >
          <AppButton
            onClick={onClose}
            variant="primary"
            customStyles={{
              borderColor:
                theme.palette.customColors?.greyDarkborder ||
                theme.palette.grey[400],
              color:
                theme.palette.customColors?.greyTextColor ||
                theme.palette.text.secondary,
              fontWeight: 600,
              padding: "10px 24px",
              borderRadius: "8px",
              "&:hover": {
                borderColor:
                  theme.palette.customColors?.greyTextColor ||
                  theme.palette.text.primary,
                backgroundColor:
                  theme.palette.customColors?.greyDarkborder ||
                  theme.palette.grey[100],
              },
            }}
            label="Cancel"
          />

          <AppButton
            onClick={handleSubmit}
            variant="primary"
            customStyles={{
              backgroundColor:
                theme.palette.customColors?.EmeraldGreen ||
                theme.palette.success.main,
              color:
                theme.palette.customColors?.white || theme.palette.common.white,
              fontWeight: 600,
              padding: "10px 24px",
              borderRadius: "8px",
              "&:hover": {
                backgroundColor:
                  theme.palette.customColors?.greenDark ||
                  theme.palette.success.dark,
                transform: "translateY(-1px)",
                boxShadow: `0 4px 12px ${
                  theme.palette.customColors?.EmeraldGreen ||
                  theme.palette.success.main
                }30`,
              },
            }}
            label="Update"
          />
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default SingleInput;
